import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import {
  formatTenantFeature,
  isTenantFeatureEnabled,
  requiredPlanForFeature,
} from "@/lib/tenantBillingAccess";
import {
  canAccessClinicModule,
  MODULE_PERMISSIONS_UPDATED,
  modulePermissionRowsToAccess,
} from "@/lib/clinicModuleAccess";

const FeaturesContext = createContext(null);
const FALLBACK_READ_ONLY_ACCESS = {
  mode: "read_only",
  reason: "tenant_access_unavailable",
  features: {},
  required_plan: {},
};

export function FeaturesProvider({ children }) {
  const { user, activeClinicId, clinicRole } = useAuth();
  const userId = user?.id || null;
  const [access, setAccess] = useState(null);
  const [moduleAccess, setModuleAccess] = useState(() => modulePermissionRowsToAccess([]));
  const [loading, setLoading] = useState(true);
  const [moduleLoading, setModuleLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moduleError, setModuleError] = useState(null);

  const refreshFeatures = useCallback(async () => {
    if (!userId && !activeClinicId) {
      setAccess(null);
      setLoading(false);
      setError(null);
      return null;
    }

    setLoading(true);
    setError(null);
    const result = await supabase.rpc("get_tenant_access_state");
    const { data, error: rpcError } = result || {};
    if (rpcError) {
      setAccess(FALLBACK_READ_ONLY_ACCESS);
      setError(rpcError);
      setLoading(false);
      return null;
    }

    setAccess(data || FALLBACK_READ_ONLY_ACCESS);
    setLoading(false);
    return data || null;
  }, [activeClinicId, userId]);

  const refreshModulePermissions = useCallback(async () => {
    if (!userId || !activeClinicId || !clinicRole) {
      setModuleAccess(modulePermissionRowsToAccess([]));
      setModuleLoading(false);
      setModuleError(null);
      return null;
    }

    setModuleLoading(true);
    setModuleError(null);
    const { data, error: rpcError } = await supabase.rpc("get_module_permissions", {
      p_role: clinicRole,
    });
    if (rpcError) {
      setModuleAccess(modulePermissionRowsToAccess([]));
      setModuleError(rpcError);
      setModuleLoading(false);
      return null;
    }

    const resolved = modulePermissionRowsToAccess(data);
    setModuleAccess(resolved);
    setModuleLoading(false);
    return resolved;
  }, [activeClinicId, clinicRole, userId]);

  useEffect(() => {
    refreshFeatures();
  }, [refreshFeatures]);

  useEffect(() => {
    refreshModulePermissions();
  }, [refreshModulePermissions]);

  useEffect(() => {
    const onImpersonationStarted = () => {
      refreshFeatures();
      refreshModulePermissions();
    };
    window.addEventListener("dental-os:impersonation-session-started", onImpersonationStarted);
    window.addEventListener(MODULE_PERMISSIONS_UPDATED, refreshModulePermissions);
    return () => {
      window.removeEventListener("dental-os:impersonation-session-started", onImpersonationStarted);
      window.removeEventListener(MODULE_PERMISSIONS_UPDATED, refreshModulePermissions);
    };
  }, [refreshFeatures, refreshModulePermissions]);

  const value = useMemo(() => ({
    access,
    loading,
    error,
    moduleAccess,
    moduleLoading,
    moduleError,
    plan: access?.plan || "clinic",
    packageSegment: access?.package_segment || "Clinic",
    features: access?.features || {},
    requiredPlan: access?.required_plan || {},
    refreshFeatures,
    refreshModulePermissions,
    hasFeature: (featureKey) => isTenantFeatureEnabled(access, featureKey),
    canAccessModule: (moduleKey) => canAccessClinicModule(moduleAccess, moduleKey),
  }), [access, loading, error, moduleAccess, moduleLoading, moduleError, refreshFeatures, refreshModulePermissions]);

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

export function useFeatures() {
  const ctx = useContext(FeaturesContext);
  if (!ctx) throw new Error("useFeatures must be used inside <FeaturesProvider>");
  return ctx;
}

export function FeatureRouteGate({ feature, anyOf, children }) {
  const location = useLocation();
  const { access, loading, hasFeature } = useFeatures();

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Checking feature access...
      </div>
    );
  }

  const featureList = Array.isArray(anyOf) && anyOf.length > 0 ? anyOf : feature ? [feature] : [];
  const granted = featureList.find((key) => hasFeature(key));
  if (granted) return children;

  const primary = featureList[0] || feature;
  const params = new URLSearchParams({
    feature: primary,
    from: location.pathname,
    required_plan: requiredPlanForFeature(access, primary),
  });
  return <Navigate to={`/upgrade?${params.toString()}`} replace />;
}

export function FeatureGate({ feature, anyOf, children, fallback = null }) {
  const { access, loading, hasFeature } = useFeatures();

  if (loading) return null;
  const featureList = Array.isArray(anyOf) && anyOf.length > 0 ? anyOf : feature ? [feature] : [];
  const granted = featureList.find((key) => hasFeature(key));
  if (granted) return children;
  if (fallback === "prompt") {
    const primary = featureList[0] || feature;
    return (
      <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
        Upgrade required: {formatTenantFeature(primary)}
      </span>
    );
  }
  if (typeof fallback === "function") return fallback({ access, feature, anyOf });
  return fallback;
}
